import { create } from 'zustand';
import {
  OptionChainData,
  OptionQuote,
  SpotData,
  KillSwitchStatus,
  NiftyOptionsModeConfig,
  OptionsSystemHealthReport,
  NoTradeDecision,
  StrategyTimelineEvent,
  optionsService,
} from '../services/optionsService';
import { formatISTTime, formatTime } from '../utils/utils';

export interface EventLogItem {
  id: string;
  timestamp: string;
  type: 'PRICE' | 'GREEK' | 'RISK' | 'STATE' | 'ADJUSTMENT' | 'KILL_SWITCH';
  message: string;
  severity: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
}

export interface MultiLegPosition {
  id: string;
  strategyName: string;
  underlying: string;
  openTime: string;
  expiry: string;
  status: 'OPEN' | 'ADJUSTED' | 'CLOSED';
  entryPremium: number;
  currentPremium: number;
  unrealizedPnl: number;
  targetProfit: number;
  maxLoss: number;
  legs: Array<{
    legId: string;
    optionType: 'CE' | 'PE';
    side: 'BUY' | 'SELL';
    strike: number;
    entryPrice: number;
    currentPrice: number;
    pnl: number;
    delta: number;
    theta: number;
  }>;
  greeks: {
    delta: number;
    gamma: number;
    theta: number;
    vega: number;
  };
  marginUsed: number;
}

interface OptionsStoreState {
  spotData: SpotData | null;
  chainData: OptionChainData | null;
  selectedExpiry: string;
  expiries: string[];
  sessionPhase: string;
  nextTransitionTime: string;
  isEntryAllowed: boolean;
  activePosition: MultiLegPosition | null;
  todayPnl: number;
  marginUtilizationPct: number;
  riskStatus: 'NORMAL' | 'WATCH' | 'BREACH';
  killSwitchStatus: KillSwitchStatus | null;
  selectedContract: OptionQuote | null;
  isInspectorOpen: boolean;
  events: EventLogItem[];
  isLoading: boolean;
  fetchErrors: Record<string, string>;
  setFetchError: (key: string, message: string | null) => void;

  // Phase 11 Hardening & Observability State
  modeConfig: NiftyOptionsModeConfig | null;
  healthReport: OptionsSystemHealthReport | null;
  noTradeDecision: NoTradeDecision | null;
  timelineEvents: StrategyTimelineEvent[];

  fetchSpotData: () => Promise<void>;
  fetchChainData: (expiry?: string) => Promise<void>;
  fetchExpiries: () => Promise<void>;
  fetchPositions: () => Promise<void>;
  fetchKillSwitch: () => Promise<void>;
  setKillSwitchLevel: (level: string, reason?: string) => Promise<void>;
  flattenPositions: () => Promise<void>;
  selectContract: (contract: OptionQuote | null) => void;
  closeInspector: () => void;
  addEvent: (item: Omit<EventLogItem, 'id' | 'timestamp'>) => void;

  // Phase 11 Actions
  fetchModeConfig: () => Promise<void>;
  fetchHealthReport: () => Promise<void>;
  fetchNoTradeDecision: () => Promise<void>;
  fetchTimeline: () => Promise<void>;
  fetchSession: () => Promise<void>;
}

export const useOptionsStore = create<OptionsStoreState>((set, get) => ({
  spotData: null,
  chainData: null,
  selectedExpiry: '',
  expiries: [],
  sessionPhase: 'MONITORING',
  nextTransitionTime: '15:15:00',
  isEntryAllowed: false,
  activePosition: null,
  todayPnl: 0,
  marginUtilizationPct: 0,
  riskStatus: 'NORMAL',
  killSwitchStatus: null,
  selectedContract: null,
  isInspectorOpen: false,
  events: [],
  isLoading: false,
  fetchErrors: {},

  setFetchError: (key: string, message: string | null) => {
    set((state) => {
      const next = { ...state.fetchErrors };
      if (message) {
        next[key] = message;
      } else {
        delete next[key];
      }
      return { fetchErrors: next };
    });
  },

  // Phase 11 State (populated by live fetches — no assumed defaults before first load)
  modeConfig: null,
  healthReport: null,
  noTradeDecision: null,
  timelineEvents: [],

  fetchSpotData: async () => {
    try {
      const data = await optionsService.getSpot('NIFTY');
      set({ spotData: data && data.spot ? data : null });
      get().setFetchError('spot', null);
    } catch (err: any) {
      set({ spotData: null });
      get().setFetchError('spot', err?.message || 'Failed to fetch live spot price');
    }
  },

  fetchChainData: async (expiry?: string) => {
    try {
      set({ isLoading: true });
      const exp = expiry || get().selectedExpiry;
      const data = await optionsService.getChain('NIFTY', exp);
      set({ chainData: data, selectedExpiry: exp, isLoading: false });
      get().setFetchError('chain', null);
    } catch (err: any) {
      set({ chainData: null, isLoading: false });
      get().setFetchError('chain', err?.message || 'Failed to fetch live option chain');
    }
  },

  fetchExpiries: async () => {
    try {
      const data = await optionsService.getExpiries('NIFTY');
      if (data && Array.isArray(data.expiries) && data.expiries.length > 0) {
        const stringExpiries: string[] = data.expiries.map((e: any) => {
          const raw = typeof e === 'string' ? e : e?.expiryDate || e?.expiry || String(e);
          return typeof raw === 'string' && raw.length >= 10 ? raw.substring(0, 10) : String(raw);
        });
        set({ expiries: stringExpiries, selectedExpiry: stringExpiries[0] || '' });
      } else {
        set({ expiries: [], selectedExpiry: '' });
      }
      get().setFetchError('expiries', null);
    } catch (err: any) {
      set({ expiries: [], selectedExpiry: '' });
      get().setFetchError('expiries', err?.message || 'Failed to fetch expiries');
    }
  },

  fetchPositions: async () => {
    try {
      const data = await optionsService.getPositions('NIFTY');
      if (data && ((Array.isArray(data.activePositions) && data.activePositions.length > 0) || (Array.isArray(data.history) && data.history.length > 0))) {
        const rawPos = (data.history && data.history.length > 0) ? data.history[0] : data.activePositions[0];
        const summaryPos = data.activePositions?.[0] || {};
        const mappedPos: MultiLegPosition = {
          id: rawPos.strategyId || rawPos.id || summaryPos.positionId || 'NS-001',
          strategyName: rawPos.strategyName || summaryPos.strategyName || 'IRON_CONDOR',
          underlying: rawPos.underlying || summaryPos.underlying || 'NIFTY',
          openTime: rawPos.entryTime ? formatISTTime(rawPos.entryTime, true) : '09:30:00 IST',
          expiry: typeof rawPos.expiry === 'string' ? rawPos.expiry.substring(0, 10) : 'CURRENT',
          status: rawPos.status || summaryPos.status || 'OPEN',
          entryPremium: Math.abs(rawPos.entryPremium || rawPos.entryCombinedPremium || 0),
          currentPremium: Math.abs(rawPos.currentValue || rawPos.currentCombinedPremium || 0),
          unrealizedPnl: rawPos.unrealizedPnl ?? rawPos.pnl ?? summaryPos.pnl ?? 0,
          targetProfit: rawPos.maxProfit || rawPos.targetProfit || 8000,
          maxLoss: Math.abs(rawPos.maxLoss || rawPos.maxLoss || 15000),
          legs: (rawPos.legs || []).map((l: any, idx: number) => ({
            legId: l.legId || `leg-${idx}`,
            optionType: l.optionType || (l.tradingSymbol?.includes('CE') ? 'CE' : 'PE'),
            side: l.side || (l.quantity < 0 ? 'SELL' : 'BUY'),
            strike: l.strike || 0,
            entryPrice: l.entryPrice || 0,
            currentPrice: l.currentPrice || l.ltp || 0,
            pnl: l.unrealizedPnl ?? l.pnl ?? 0,
            delta: l.greeks?.delta ?? l.delta ?? 0,
            theta: l.greeks?.theta ?? l.theta ?? 0,
          })),
          greeks: rawPos.greeks || rawPos.currentGreeks || summaryPos.currentGreeks || {
            delta: 0,
            gamma: 0,
            theta: 0,
            vega: 0,
          },
          marginUsed: rawPos.marginUsed || rawPos.marginRequired || 70000,
        };
        set({ activePosition: mappedPos, todayPnl: mappedPos.unrealizedPnl });
      } else {
        // No active positions
        set({ activePosition: null, todayPnl: 0 });
      }
      get().setFetchError('positions', null);
    } catch (err: any) {
      get().setFetchError('positions', err?.message || 'Failed to fetch positions');
    }
  },

  fetchKillSwitch: async () => {
    try {
      const status = await optionsService.getKillSwitchStatus();
      if (status && typeof status === 'object') {
        set({ killSwitchStatus: status });
      }
      get().setFetchError('killSwitch', null);
    } catch (err: any) {
      get().setFetchError('killSwitch', err?.message || 'Failed to fetch kill-switch status');
    }
  },

  setKillSwitchLevel: async (level: string, reason?: string) => {
    try {
      const status = await optionsService.setKillSwitchLevel(level, reason);
      if (status && typeof status === 'object') {
        set({ killSwitchStatus: status });
      }
      get().addEvent({
        type: 'KILL_SWITCH',
        message: `Kill-switch changed to ${level}: ${reason || 'User action'}`,
        severity: level.includes('LEVEL_0') ? 'SUCCESS' : 'WARNING',
      });
    } catch (err: any) {
      console.error('Failed to set kill switch:', err);
    }
  },

  flattenPositions: async () => {
    try {
      const status = await optionsService.flattenPositions();
      if (status && typeof status === 'object') {
        set({ killSwitchStatus: status });
      }
      get().addEvent({
        type: 'KILL_SWITCH',
        message: 'EMERGENCY FLATTEN TRIGGERED: Force squaring off all positions',
        severity: 'ERROR',
      });
    } catch (err: any) {
      console.error('Failed to flatten positions:', err);
    }
  },

  selectContract: (contract: OptionQuote | null) => {
    set({ selectedContract: contract, isInspectorOpen: !!contract });
  },

  closeInspector: () => {
    set({ isInspectorOpen: false });
  },

  addEvent: (item) => {
    const now = new Date();
    const timeStr = formatISTTime(now, true);
    const newEvent: EventLogItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      timestamp: timeStr,
      ...item,
    };
    set((state) => ({
      events: [newEvent, ...state.events.slice(0, 49)],
    }));
  },

  // Phase 11 Actions
  fetchModeConfig: async () => {
    try {
      const config = await optionsService.getMode();
      if (config && typeof config === 'object') {
        set({ modeConfig: config });
      }
      get().setFetchError('modeConfig', null);
    } catch (err: any) {
      get().setFetchError('modeConfig', err?.message || 'Failed to fetch trading mode config');
    }
  },

  fetchHealthReport: async () => {
    try {
      const report = await optionsService.getSystemHealth();
      if (report && typeof report === 'object') {
        set({ healthReport: report, isEntryAllowed: !!report.isEntriesAllowed });
      }
      get().setFetchError('healthReport', null);
    } catch (err: any) {
      // A failed health check must not silently imply entries are safe — lock out until it recovers.
      set({ isEntryAllowed: false });
      get().setFetchError('healthReport', err?.message || 'Failed to fetch system health report');
    }
  },

  fetchNoTradeDecision: async () => {
    try {
      const decision = await optionsService.getNoTradeDecision();
      if (decision && typeof decision === 'object' && Array.isArray(decision.gates)) {
        set({ noTradeDecision: decision });
      }
      get().setFetchError('noTradeDecision', null);
    } catch (err: any) {
      get().setFetchError('noTradeDecision', err?.message || 'Failed to fetch no-trade decision');
    }
  },

  fetchTimeline: async () => {
    try {
      const events = await optionsService.getTimeline();
      if (Array.isArray(events)) {
        set({ timelineEvents: events });
      }
      get().setFetchError('timeline', null);
    } catch (err: any) {
      get().setFetchError('timeline', err?.message || 'Failed to fetch strategy timeline');
    }
  },

  fetchSession: async () => {
    try {
      const sess = await optionsService.getSessionToday();
      if (sess && sess.currentState) {
        const now = new Date();
        const istHours = (now.getUTCHours() + 5 + Math.floor((now.getUTCMinutes() + 30) / 60)) % 24;
        let nextTime = '15:15:00';
        if (istHours < 9) nextTime = '09:15:00';
        else if (istHours === 9 && now.getUTCMinutes() < 25) nextTime = '09:25:00';
        else if (istHours < 14 || (istHours === 14 && now.getUTCMinutes() < 45)) nextTime = '14:45:00';
        else if (istHours < 15 || (istHours === 15 && now.getUTCMinutes() < 15)) nextTime = '15:15:00';
        else if (istHours < 15 || (istHours === 15 && now.getUTCMinutes() < 30)) nextTime = '15:30:00';
        else nextTime = '16:00:00';

        const entryAllowed = ['SIGNAL_EVALUATION', 'MONITORING'].includes(sess.currentState);
        set({
          sessionPhase: sess.currentState,
          nextTransitionTime: nextTime,
          isEntryAllowed: entryAllowed,
        });
      }
    } catch {
      // Retain existing state if session fetch fails
    }
  },
}));
